import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BadgeIcon from "@mui/icons-material/Badge";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCardIcon from "@mui/icons-material/AddCard";
import { API_URL } from "./config";

const WalletSelfServicePage = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(240,120,40,0.08)";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_FOCUS,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      backgroundColor: "rgba(25,118,210,0.95)",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const token = localStorage.getItem("auth");

  const authConfig = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [idCardInfo, setIdCardInfo] = useState(null);

  const [recharge, setRecharge] = useState({
    amount: "",
    remarks: "",
  });

  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const decodeToken = () => {
    try {
      if (!token) return {};
      const cleanToken = token.startsWith("Bearer ") ? token.slice(7) : token;
      return JSON.parse(atob(cleanToken.split(".")[1]));
    } catch {
      return {};
    }
  };

  const tokenPayload = useMemo(() => decodeToken(), [token]);

  const fallbackDisplayName =
    tokenPayload?.fullname ||
    tokenPayload?.FullName ||
    tokenPayload?.username ||
    tokenPayload?.StudentID ||
    tokenPayload?.EmployeeID ||
    "-";

  const fallbackPersonCode =
    tokenPayload?.StudentID ||
    tokenPayload?.studentid ||
    tokenPayload?.EmployeeID ||
    tokenPayload?.employeeid ||
    tokenPayload?.username ||
    "-";

  const displayName =
    idCardInfo?.FullName ||
    idCardInfo?.StudentName ||
    idCardInfo?.EmployeeName ||
    fallbackDisplayName;

  const personCode =
    idCardInfo?.StudentID ||
    idCardInfo?.EmployeeID ||
    idCardInfo?.PersonID ||
    fallbackPersonCode;

  const formatMoney = (v) => `₹${Number(v || 0).toFixed(2)}`;

  const formatLocalDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  };

  const loadScript = (src) => {
    return new Promise((resolve) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
      const s = document.createElement("script");
      s.src = src;
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
  };

  const fetchMyWallet = async () => {
    try {
      if (!token) {
        setError("Access denied: No token provided");
        return;
      }

      const res = await axios.get(`${API_URL}/api/wallet/me`, authConfig);

      setWallet(res.data?.wallet || null);
      setLedger(Array.isArray(res.data?.ledger) ? res.data.ledger : []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to load wallet");
    }
  };

  const fetchMyIdCardMeta = async () => {
    try {
      if (!token) {
        setError("Access denied: No token provided");
        return;
      }

      const res = await axios.get(`${API_URL}/api/wallet/idcards/me/meta`, authConfig);
      setIdCardInfo(res.data?.idCard || res.data || null);
    } catch (err) {
      console.error(err);
      setIdCardInfo(null);
      setError(err?.response?.data?.message || "Failed to load ID card details");
    }
  };

  useEffect(() => {
    fetchMyWallet();
    fetchMyIdCardMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    await fetchMyWallet();
    await fetchMyIdCardMeta();
  };

const handleViewIdCard = async () => {
  try {
    if (!token) {
      setError("Access denied: No token provided");
      return;
    }

    const res = await axios.get(`${API_URL}/api/wallet/idcards/me/view`, {
      ...authConfig,
      responseType: "blob",
    });

    const blob = new Blob([res.data], {
      type: res.headers["content-type"] || "text/html",
    });

    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener,noreferrer");

    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 15000);
  } catch (err) {
    console.error("View ID card error:", err);
    setError(err?.response?.data?.message || "Failed to view ID card");
  }
};

const handleDownloadIdCard = async () => {
  try {
    if (!token) {
      setError("Access denied: No token provided");
      return;
    }

    const res = await axios.get(`${API_URL}/api/wallet/idcards/me/download`, {
      ...authConfig,
      responseType: "blob",
    });

    const blob = new Blob([res.data], {
      type: res.headers["content-type"] || "text/html",
    });

    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;

    const contentDisposition = res.headers["content-disposition"] || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    a.download = match?.[1] || `${personCode}_idcard.html`;

    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error(err);
    setError(err?.response?.data?.message || "Failed to download ID card");
  }
};

  const handleRecharge = async () => {
    try {
      setError("");
      setPayMsg(null);

      if (!token) {
        setError("Access denied: No token provided");
        return;
      }

      if (!wallet?.WalletAccountId) {
        setError("Wallet not found");
        return;
      }

      if (!recharge.amount || Number(recharge.amount) <= 0) {
        setError("Enter a valid amount");
        return;
      }

      setPayBusy(true);

      const idem = `SELF_WALLET_${wallet.WalletAccountId}_${Date.now()}`;

      const { data } = await axios.post(
        `${API_URL}/api/wallet/me/recharge/create-order`,
        {
          amount: Number(recharge.amount),
          remarks: recharge.remarks || null,
          idempotencyKey: idem,
          mode: "LIVE",
        },
        authConfig
      );

      if (data.Gateway === "RAZORPAY") {
        const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
        if (!ok) throw new Error("Failed to load Razorpay");

        const rzp = new window.Razorpay({
          key: data.RazorpayKeyId,
          amount: Math.round(Number(data.Amount) * 100),
          currency: data.Currency || "INR",
          name: "Campus Wallet Recharge",
          description: data.ClientTxnRef || `Wallet ${wallet.WalletAccountId}`,
          order_id: data.RazorpayOrderId,
          handler: async (resp) => {
            try {
              await axios.post(
                `${API_URL}/api/wallet/me/recharge/confirm`,
                {
                  rechargeOrderId: data.RechargeOrderId,
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                },
                authConfig
              );

              setRecharge({
                amount: "",
                remarks: "",
              });

              setPayMsg({
                type: "success",
                text: "Wallet recharge completed successfully",
              });

              await fetchMyWallet();
              setSuccess("Wallet recharge completed successfully");
            } catch (e) {
              console.error(e);
              setPayMsg({
                type: "error",
                text:
                  e?.response?.data?.message ||
                  "Payment confirmed but wallet update failed.",
              });
            }
          },
          modal: {
            ondismiss: () => {
              setPayMsg({ type: "warning", text: "Payment window closed." });
            },
          },
          theme: {
            color: THEME_ORANGE,
          },
        });

        rzp.open();
        return;
      }

      if (data.Gateway === "PAYTM") {
        const ok = await loadScript(
          "https://securegw-stage.paytm.in/merchantpgpui/checkoutjs/merchants/" +
            data.PaytmMid +
            ".js"
        );
        if (!ok) throw new Error("Failed to load Paytm CheckoutJS");

        const config = {
          root: "",
          flow: "DEFAULT",
          data: {
            orderId: data.PaytmOrderId,
            token: data.PaytmTxnToken,
            tokenType: "TXN_TOKEN",
            amount: String(data.Amount),
          },
        };

        setPayMsg({ type: "info", text: "Opening Paytm..." });
        await window.Paytm.CheckoutJS.init(config);
        window.Paytm.CheckoutJS.invoke();
        return;
      }

      if (data.Gateway === "BILLDESK") {
        if (data.Redirect?.gatewayUrl && data.Redirect?.formFields) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = data.Redirect.gatewayUrl;

          Object.entries(data.Redirect.formFields).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
          return;
        }

        throw new Error("Invalid BillDesk redirect payload");
      }

      throw new Error("Unsupported gateway from server");
    } catch (err) {
      console.error(err);
      setPayMsg(null);
      setError(err?.response?.data?.message || err.message || "Payment failed");
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              My ID Card & Wallet
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              View or download ID card and recharge your wallet
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{
              color: "#fff",
              borderColor: "rgba(255,255,255,0.55)",
              "&:hover": {
                borderColor: "#fff",
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            Refresh
          </Button>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          {!!payMsg && (
            <Alert severity={payMsg.type} onClose={() => setPayMsg(null)} sx={{ mb: 2 }}>
              {payMsg.text}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <BadgeIcon sx={{ color: THEME_ORANGE }} />
                  <Typography sx={{ fontWeight: 900 }}>ID Card</Typography>
                </Stack>

                <Stack spacing={1}>
                  <Typography><b>Name:</b> {displayName}</Typography>
                  <Typography><b>ID:</b> {personCode}</Typography>
                  <Typography><b>Card Type:</b> {idCardInfo?.CardType || "-"}</Typography>
                  <Typography><b>Card Serial:</b> {idCardInfo?.CardSerialNo || "-"}</Typography>
                  <Typography><b>Card Status:</b> {idCardInfo?.Status || "-"}</Typography>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<BadgeIcon />}
                    sx={primaryBtnSx}
                    onClick={handleViewIdCard}
                  >
                    View ID Card
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    sx={outlinedBtnSx}
                    onClick={handleDownloadIdCard}
                  >
                    Download ID Card
                  </Button>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <AccountBalanceWalletIcon sx={{ color: THEME_ORANGE }} />
                  <Typography sx={{ fontWeight: 900 }}>Recent Wallet Transactions</Typography>
                </Stack>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`Current Balance: ${formatMoney(wallet?.CurrentBalance)}`}
                    sx={{
                      fontWeight: 900,
                      color: "#fff",
                      backgroundColor: "rgba(25,118,210,0.95)",
                    }}
                  />
                </Box>

                <Box sx={{ overflowX: "auto" }}>
                  <TableContainer component="div">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell>#</TableCell>
                          <TableCell>Txn Type</TableCell>
                          <TableCell>Direction</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Balance After</TableCell>
                          <TableCell>Created At</TableCell>
                          <TableCell>Remarks</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ledger.length ? (
                          ledger.map((l, idx) => (
                            <TableRow key={l.LedgerId || idx} sx={zebraRowSx(idx)}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>{l.TxnType || "-"}</TableCell>
                              <TableCell>{l.Direction || "-"}</TableCell>
                              <TableCell>{formatMoney(l.Amount)}</TableCell>
                              <TableCell>{formatMoney(l.BalanceAfter)}</TableCell>
                              <TableCell>{formatLocalDateTime(l.CreatedAt)}</TableCell>
                              <TableCell>{l.Remarks || "-"}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow sx={zebraRowSx(0)}>
                            <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                              No wallet transactions found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 900, mb: 1.5 }}>
                  Recharge Wallet
                </Typography>

                <Stack spacing={2}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Your wallet will be recharged against your own login account only.
                  </Alert>

                  <TextField
                    label="Amount"
                    type="number"
                    value={recharge.amount}
                    onChange={(e) => setRecharge((s) => ({ ...s, amount: e.target.value }))}
                    size="small"
                    fullWidth
                    sx={inputSx}
                  />

                  <TextField
                    label="Remarks"
                    value={recharge.remarks}
                    onChange={(e) => setRecharge((s) => ({ ...s, remarks: e.target.value }))}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={inputSx}
                  />

                  <Button
                    variant="contained"
                    startIcon={<AddCardIcon />}
                    sx={primaryBtnSx}
                    onClick={handleRecharge}
                    disabled={!wallet || payBusy}
                  >
                    {payBusy ? "Starting..." : "Recharge Now"}
                  </Button>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                <Typography sx={{ fontWeight: 900, mb: 1.5 }}>
                  Wallet Summary
                </Typography>

                <Stack spacing={1}>
                  <Typography><b>Wallet Account ID:</b> {wallet?.WalletAccountId || "-"}</Typography>
                  <Typography><b>Card Type:</b> {wallet?.CardType || "-"}</Typography>
                  <Typography><b>Wallet Status:</b> {wallet?.WalletStatus || "-"}</Typography>
                  <Typography><b>Current Balance:</b> {formatMoney(wallet?.CurrentBalance)}</Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default WalletSelfServicePage;